import { Component, ReactNode } from 'react';
import { logger } from '../../services/loggingService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class PlanModeErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log immediately to console for debugging (error boundaries need immediate visibility)
    console.error('Plan Mode Error:', error, errorInfo);
    // Also log via logger for centralized monitoring
    logger.error('Plan Mode ErrorBoundary caught an error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-base flex items-center justify-center p-6">
          <div className="card-base rounded-lg shadow-lg p-8 max-w-2xl">
            <div className="text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-primary mb-4">
                Something went wrong
              </h2>
              <p className="text-secondary mb-6">
                An error occurred in Plan Mode. Please try refreshing the page.
              </p>
              {this.state.error && (
                <details className="text-left bg-subtle p-4 rounded-lg mb-6">
                  <summary className="cursor-pointer font-semibold text-primary mb-2">
                    Error Details
                  </summary>
                  <pre className="text-xs text-error overflow-auto">
                    {this.state.error.message}
                    {'\n\n'}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-primary"
                >
                  Refresh Page
                </button>
                <button
                  onClick={() => this.setState({ hasError: false, error: null })}
                  className="btn btn-secondary"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
